/**
 * ProjeBeyaniUyarilariTab.jsx — Proje Beyanı Uyarıları
 * Fuar Kuralları ekranının (FuarKurallariPage.jsx) "Beyan Uyarıları" sekmesi
 * (/Management/Fairs/Rules?tab=uyari). 2026-09-15'e kadar ayrı bir sayfaydı
 * (ProjeBeyaniUyarilariPage.jsx, /Management/Projects/DeclarationWarnings — o yol
 * artık buraya yönleniyor).
 *
 * Denetçi/admin ekranı: dekoratörün "Proje Beyanı" formunu doldururken (bkz.
 * CreateProjectModal, FirmaProjelerListPage.jsx) Sınıflandırma/Stand Türü
 * Seçimi bölümü DIŞINDAKİ her alanın altına gösterilecek serbest metin bir
 * uyarı tanımlar — örn. "Ahşap standlar bu fuarda 24m²'den küçük olamaz."
 * altında Proje Alanı alanı. Alan kataloğu sabit 5 satır (bkz.
 * projeBeyaniUyariService.js DECLARATION_FIELDS), uyarılar seçili fuara özel.
 *
 * Aynı ekranın "Fuar Kuralları" sekmesi bu cümlelerin çoğunu backend'in ZORLADIĞI
 * kurallara çeviriyor; serbest metin uyarılar kuralı olmayan şeyler için duruyor.
 *
 * Bu sürümde sadece yönetim ekranı — uyarıların dekoratör tarafındaki Proje
 * Beyanı modalına canlı gösterimi henüz bağlanmadı (bilinçli karar, ayrı bir
 * işe bırakıldı).
 */

import React, { useState, useEffect } from "react";
import {
  Card, CardBody, CardHeader,
  Button, Badge, Table,
  Modal, ModalHeader, ModalBody, ModalFooter,
  Alert, Spinner, Input, FormGroup, Label,
} from "reactstrap";
import { Pencil, Eraser, RefreshCw, AlertTriangle } from "lucide-react";
import {
  getDeclarationWarnings,
  setDeclarationWarning,
  clearDeclarationWarning,
} from "../../../services/projeBeyaniUyariService";
import { useTranslation } from "react-i18next";
import { currentLocaleTag } from "../../../i18n";

export default function ProjeBeyaniUyarilariTab({ fuarId }) {
  const { t: tx } = useTranslation("yonetim");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [actionLoading, setActionLoading] = useState(false);

  const [editModal, setEditModal] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editText, setEditText] = useState("");
  const [editActive, setEditActive] = useState(true);

  useEffect(() => {
    if (!successMsg) return undefined;
    const t = setTimeout(() => setSuccessMsg(null), 3000);
    return () => clearTimeout(t);
  }, [successMsg]);

  // KURAL: fetch mantığı bilinçli olarak Effect içinde tanımlanır (üst seviye
  // paylaşılan bir fonksiyon değil) — aksi halde react-hooks/set-state-in-effect
  // ihlali oluşur. Yenile butonu / kaydet-kaldır işlemleri reloadToken'ı
  // artırarak yeniden çekmeyi tetikler.
  useEffect(() => {
    if (!fuarId) return undefined;
    let ignore = false;

    async function run() {
      setLoading(true);
      setError(null);
      try {
        const data = await getDeclarationWarnings(fuarId);
        if (!ignore) setRows(data || []);
      } catch (e) {
        if (!ignore) setError(e.message);
      } finally {
        if (!ignore) setLoading(false);
      }
    }

    run();
    return () => { ignore = true; };
  }, [fuarId, reloadToken]);

  function openEditModal(row) {
    setEditRow(row);
    setEditText(row.text);
    setEditActive(row.text ? row.active : true);
    setEditModal(true);
  }

  async function handleSaveWarning() {
    if (!editRow || !fuarId || !editText.trim()) return;
    setActionLoading(true);
    setError(null);
    try {
      await setDeclarationWarning(fuarId, editRow.fieldKey, {
        text: editText.trim(), active: editActive,
      });
      setEditModal(false);
      setReloadToken((t) => t + 1);
      setSuccessMsg(tx("projeBeyaniUyarilariTab.uyariKaydedildi"));
    } catch (e) {
      setError(e.message);
    } finally {
      setActionLoading(false);
    }
  }

  async function handleClearWarning(row) {
    if (!fuarId) return;
    setActionLoading(true);
    setError(null);
    try {
      await clearDeclarationWarning(fuarId, row.fieldKey);
      setReloadToken((t) => t + 1);
      setSuccessMsg(tx("projeBeyaniUyarilariTab.uyariKaldirildi"));
    } catch (e) {
      setError(e.message);
    } finally {
      setActionLoading(false);
    }
  }

  return (
    <>
      {successMsg && <Alert color="success">{successMsg}</Alert>}
      {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}

      <Card>
        <CardHeader className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
            <AlertTriangle size={16} className="me-2 text-warning" />
            {tx("projeBeyaniUyarilariTab.projeBeyaniFormuAlanUyarilari")}
          </h5>
          <Button
            color="light" size="sm" className="border"
            onClick={() => setReloadToken((t) => t + 1)}
            disabled={loading}
            title={tx("common:yenile")}
          >
            <RefreshCw size={14} />
          </Button>
        </CardHeader>
        <CardBody>
          {loading ? (
            <div className="text-center py-4"><Spinner color="primary" /></div>
          ) : (
            <div className="table-responsive">
              <Table hover bordered size="sm" className="align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>{tx("projeBeyaniUyarilariTab.bolum")}</th>
                    <th>{tx("projeBeyaniUyarilariTab.alan")}</th>
                    <th>{tx("projeBeyaniUyarilariTab.uyariMetni")}</th>
                    <th>{tx("projeBeyaniUyarilariTab.durum")}</th>
                    <th>{tx("projeBeyaniUyarilariTab.sonGuncelleme")}</th>
                    <th>{tx("projeBeyaniUyarilariTab.islem")}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.fieldKey}>
                      <td className="text-muted small">{r.section}</td>
                      <td className="fw-semibold">{r.fieldLabel}</td>
                      <td>
                        {r.text ? (
                          <span>{r.text}</span>
                        ) : (
                          <span className="text-muted small">{tx("projeBeyaniUyarilariTab.uyariTanimlanmamis")}</span>
                        )}
                      </td>
                      <td>
                        {r.text ? (
                          <Badge color={r.active ? "success" : "secondary"}>
                            {r.active ? tx("projeBeyaniUyarilariTab.aktif") : tx("projeBeyaniUyarilariTab.pasif")}
                          </Badge>
                        ) : (
                          <span className="text-muted small">—</span>
                        )}
                      </td>
                      <td className="text-muted small">
                        {r.updatedAt ? new Date(r.updatedAt).toLocaleDateString(currentLocaleTag()) : "—"}
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          <Button
                            color="warning" size="sm" outline
                            onClick={() => openEditModal(r)}
                            disabled={actionLoading}
                            title={tx("common:duzenle")}
                          >
                            <Pencil size={12} />
                          </Button>
                          {r.text && (
                            <Button
                              color="danger" size="sm" outline
                              onClick={() => handleClearWarning(r)}
                              disabled={actionLoading}
                              title={tx("projeBeyaniUyarilariTab.kaldir")}
                            >
                              <Eraser size={12} />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </CardBody>
      </Card>

      {/* ─── MODAL: Uyarı Düzenle ─── */}
      <Modal isOpen={editModal} toggle={() => setEditModal(false)}>
        <ModalHeader toggle={() => setEditModal(false)}>
          {tx("projeBeyaniUyarilariTab.uyariDuzenle", { fieldLabel: editRow?.fieldLabel })}
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label>{tx("projeBeyaniUyarilariTab.uyariMetni")}</Label>
            <Input
              type="textarea" rows={3}
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              placeholder={tx("projeBeyaniUyarilariTab.ornAhsapStandlarBuFuarda")}
            />
          </FormGroup>
          <FormGroup switch className="mb-0">
            <Input
              type="switch" role="switch"
              checked={editActive}
              onChange={(e) => setEditActive(e.target.checked)}
            />
            <Label check>{tx("projeBeyaniUyarilariTab.aktifDekoratoreGosterilsin")}</Label>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setEditModal(false)} disabled={actionLoading}>{tx("common:iptal")}</Button>
          <Button color="primary" onClick={handleSaveWarning} disabled={actionLoading || !editText.trim()}>
            {actionLoading ? <Spinner size="sm" /> : tx("common:kaydet")}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}
